import type {
  DialogNode,
  SarahAction,
  FormContent,
  UserChoiceValue,
  FormField,
} from '../types';
import { CookieStore } from './cookie-store';

/**
 * Regex email — port 1:1 du validateur Angular d'origine.
 * Limite la longueur totale et la longueur du local-part, autorise les
 * caractères spéciaux RFC 5322, et exige un domaine "propre" sans tiret final.
 */
export const EMAIL_RE =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Valide une valeur de champ selon les règles déclarées sur le nœud form.
 * Retourne le message d'erreur de la première règle violée, ou null si valide.
 *
 * Comportement aligné sur l'Angular d'origine : `email` et `pattern` ne
 * déclenchent pas sur une valeur vide ; seul `required` rejette le vide.
 */
export function validateField(field: FormField, value: unknown): string | null {
  const rules = field.validations || [];
  for (const r of rules) {
    if (r.name === 'required' && (value === '' || value == null)) return r.message;
    if (r.name === 'email' && value && !EMAIL_RE.test(String(value))) return r.message;
    if (r.name === 'pattern' && value && r.validator) {
      if (!new RegExp(r.validator).test(String(value))) return r.message;
    }
  }
  return null;
}

/**
 * Moteur de navigation dans l'arbre de dialogue (port du SarahService Angular).
 *
 * État porté :
 *  - `currentKey` : clé du nœud courant (utilisée par `getIndexBefore` pour
 *    retrouver sa position dans l'historique).
 *  - `action` : prochaine intention (`first` au boot, `other`/`button` pour
 *    une navigation avant, `back` pour reculer).
 *
 * Effets de bord (cookies) :
 *  - `user_choices` : map clé-de-nœud → choix utilisateur (string ou FormContent).
 *  - `user_map` : pile ordonnée des clés visitées (pour le back).
 *  - `user_visitor` : prénom mémorisé depuis le formulaire d'identité (clé
 *    `formPrenom`), affiché dans l'écran d'attente.
 */
export class DialogEngine {
  currentKey = '';
  action: SarahAction = { is: 'first', key: null };

  constructor(
    private readonly dialog: DialogNode[],
    private readonly cookies: CookieStore,
  ) {}

  /**
   * Récupère le nœud à afficher en fonction de l'action courante.
   *
   * Sauf en `back`, sauvegarde le choix (`saveUserChoices`) et empile la clé
   * dans `user_map` (`saveUserStep`). Au démarrage (`first`), `user_map` est
   * vidé pour repartir d'un historique propre.
   *
   * Pour un nœud `msg`, calcule les enfants groupés par type. Les `choice`
   * sont triés par `ordre` (ordre visuel garanti) ; `form` et `msg` sont
   * laissés dans l'ordre du tableau source.
   */
  getMessage(reference: string = '', content: FormContent | null = null): DialogNode | null {
    reference = reference || '';
    const is = this.action.is;
    const key = this.action.key;

    if (is !== 'back') {
      this.currentKey = reference;
      if (reference !== '') {
        this.saveUserChoices(reference, key, content);
        this.saveUserStep(reference);
      }
      if (is === 'first') this.cookies.setJSON('user_map', []);
    } else if (key !== null) {
      this.currentKey = key;
    }

    let response: DialogNode | undefined;
    if (is !== 'back') {
      response = this.dialog.filter((n) => n.pere === key)[0];
    } else {
      response = this.dialog.filter((n) => n.cle === key)[0];
    }
    if (!response) return null;

    if (response.type === 'msg') {
      const fils = this.dialog.filter((n) => n.pere === response.cle);
      response.children = {
        choices: fils
          .filter((n) => n.type === 'choice')
          .sort((a, b) => a.ordre - b.ordre),
        form: fils.filter((n) => n.type === 'form'),
        msg: fils.filter((n) => n.type === 'msg'),
      };
    }
    return response;
  }

  getLastValue(cle: string): { key: string; data: UserChoiceValue | '' } {
    const choices = this.cookies.getJSON<Record<string, UserChoiceValue>>('user_choices', {});
    return { key: cle, data: choices[cle] || '' };
  }

  /**
   * Persiste le choix de l'utilisateur pour un nœud donné. Si `content`
   * correspond à `value` (cas d'un formulaire), on stocke l'objet structuré
   * `{ key, value }` ; sinon une simple clé de choix.
   *
   * Cas particulier : la sous-clé `formPrenom` est aussi recopiée dans
   * `user_visitor` pour pouvoir personnaliser l'écran d'attente sans relire
   * le formulaire entier.
   */
  saveUserChoices(key: string, value: string | null, content: FormContent | null): void {
    const choices = this.cookies.getJSON<Record<string, UserChoiceValue>>('user_choices', {});
    let entry: UserChoiceValue;
    if (content && value !== null && content.key === value) {
      const tmp: Record<string, unknown> = {};
      Object.entries(content.value).forEach(([k, v]) => {
        tmp[k] = v;
        if (k === 'formPrenom') this.cookies.set('user_visitor', JSON.stringify(v));
      });
      entry = { key: value, value: tmp };
    } else {
      // `value` peut être null si on est en train d'enregistrer le tout
      // premier nœud sans action utilisateur (cas dégénéré : conservé pour
      // rester aligné avec le SarahService Angular d'origine).
      entry = value ?? '';
    }
    choices[key] = entry;
    this.cookies.setJSON('user_choices', choices);
  }

  saveUserStep(key: string): void {
    const map = this.cookies.getJSON<string[]>('user_map', []);
    if (!map.includes(key)) {
      map.push(key);
      this.cookies.setJSON('user_map', map);
    }
  }

  /**
   * Dépile l'historique pour revenir en arrière.
   *
   * Le retour cible le dernier élément empilé (`map[map.length - 1]`).
   * On supprime au passage `currentKey` s'il est dans la pile (cas standard),
   * sinon on supprime le dernier élément (filet de sécurité historique du
   * code Angular d'origine).
   */
  getIndexBefore(): string | null {
    const map = this.cookies.getJSON<string[]>('user_map', []);
    if (map.length === 0) return null;
    const idx = map.indexOf(this.currentKey);
    const retour = map[map.length - 1];
    if (idx > -1) map.splice(idx, 1);
    else map.splice(map.length - 1, 1);
    this.cookies.setJSON('user_map', map);
    return retour;
  }

  /**
   * Construit le payload envoyé à `/user-datas` : ne contient que les choix
   * dont la clé figure dans `user_map` (i.e. effectivement visitée), pour
   * écarter d'éventuels reliquats d'un parcours antérieur.
   *
   * Normalise aussi les valeurs avant envoi : pour les champs de type select
   * (notamment `formDept` qui stocke en mémoire l'objet `Departement` complet
   * pour l'affichage), on extrait juste l'identifiant. Le back attend une
   * valeur scalaire pour ces colonnes.
   */
  buildUserDataPayload(): Array<[string, UserChoiceValue]> {
    const map = this.cookies.getJSON<string[]>('user_map', []);
    const choices = this.cookies.getJSON<Record<string, UserChoiceValue>>('user_choices', {});
    const payload: Array<[string, UserChoiceValue]> = [];
    for (const [k, v] of Object.entries(choices)) {
      if (map.includes(k)) payload.push([k, this.sanitizeForBackend(v)]);
    }
    return payload;
  }

  private sanitizeForBackend(v: UserChoiceValue): UserChoiceValue {
    if (typeof v !== 'object' || v === null || !('key' in v) || !('value' in v)) return v;
    const cleanedValue: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v.value)) {
      if (val && typeof val === 'object' && 'id_departement' in (val as object)) {
        cleanedValue[k] = (val as { id_departement: number }).id_departement;
      } else {
        cleanedValue[k] = val;
      }
    }
    return { key: v.key, value: cleanedValue };
  }

  /**
   * Vide les cookies de session Sarah. Les tokens d'auth (`a-*`) sont
   * conservés : ils relèvent d'un cycle de vie indépendant (login final).
   */
  reset(): void {
    this.cookies.delete('user_choices');
    this.cookies.delete('user_map');
    this.cookies.delete('user_visitor');
    this.currentKey = '';
    this.action = { is: 'first', key: null };
  }
}
